import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Empty, PageHeader, inputCls } from "@/components/Common";
import { PackageCard } from "@/components/PackageCard";
import { OfferBanner } from "@/components/Offers";
import { http } from "@/lib/api";

const IMG = "https://images.unsplash.com/photo-1697898109604-e06e88b15271?auto=format&fit=crop&w=1600&q=80";

export default function Packages() {
  const [sp, setSp] = useSearchParams();
  const region = sp.get("region") || "";
  const [term, setTerm] = useState("");
  const [sort, setSort] = useState("price");
  const { data = [], isLoading } = useQuery({ queryKey: ["packages", region], queryFn: () => http.get(`/packages${region ? `?region=${region}` : ""}`).then((r) => r.data) });
  const shown = useMemo(() => {
    const t = term.trim().toLowerCase();
    const rows = data.filter((p) => !t || `${p.name} ${p.location} ${p.country}`.toLowerCase().includes(t));
    return [...rows].sort((a, b) => (sort === "price" ? a.price - b.price : sort === "price-desc" ? b.price - a.price : a.days - b.days));
  }, [data, term, sort]);

  return (
    <div data-testid="packages-page">
      <PageHeader eyebrow="Holiday packages" title="Destinations & itineraries" sub="80 handpicked tours across India and the world — hotels, sightseeing and transfers included." image={IMG} />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <OfferBanner />
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {[["", "All"], ["domestic", "India"], ["international", "International"]].map(([k, l]) => (
            <button key={k} onClick={() => setSp(k ? { region: k } : {})} data-testid={`packages-filter-${l.toLowerCase()}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${region === k ? "bg-[#0D1B2A] text-white" : "bg-white border border-[#E5E0D8] text-slate-600"}`}>{l}</button>
          ))}
          <div className="relative ml-auto w-full sm:w-72">
            <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
            <input className={`${inputCls} pl-9`} placeholder="Search Kashmir, Japan, beaches…" value={term} onChange={(e) => setTerm(e.target.value)} data-testid="packages-search-input" />
          </div>
          <select className={`${inputCls} w-full sm:w-48`} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="packages-sort-select">
            <option value="price">Price: low to high</option><option value="price-desc">Price: high to low</option><option value="days">Duration</option>
          </select>
        </div>
        <p className="mt-6 text-sm text-slate-500" data-testid="packages-count">{shown.length} packages</p>
        <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading && [1, 2, 3].map((i) => <div key={i} className="h-80 animate-pulse rounded-2xl bg-white" />)}
          {shown.map((p, i) => <PackageCard key={p.id} p={p} i={i % 9} />)}
        </div>
        {!isLoading && !shown.length && <Empty testid="packages-empty">No packages match “{term}”.</Empty>}
      </div>
    </div>
  );
}
